import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN = 8;
const CLIP_OVERFLOW = new Set(["auto", "scroll", "hidden", "clip"]);

function getClipBounds(element) {
    let left = VIEWPORT_MARGIN;
    let top = VIEWPORT_MARGIN;
    let right = window.innerWidth - VIEWPORT_MARGIN;
    let bottom = window.innerHeight - VIEWPORT_MARGIN;
    let scrollParent = null;

    let parent = element.parentElement;
    while (parent) {
        const style = window.getComputedStyle(parent);
        const rect = parent.getBoundingClientRect();

        if (CLIP_OVERFLOW.has(style.overflowX)) {
            left = Math.max(left, rect.left);
            right = Math.min(right, rect.right);
            if (!scrollParent) {
                scrollParent = parent;
            }
        }

        if (CLIP_OVERFLOW.has(style.overflowY)) {
            top = Math.max(top, rect.top);
            bottom = Math.min(bottom, rect.bottom);
        }

        if (parent === document.body) {
            break;
        }

        parent = parent.parentElement;
    }

    return { left, top, right, bottom, scrollParent };
}

// Long timeline bars keep their real left edge after horizontal scroll.
// Position against the painted slice, and step past sticky columns that cover it.
function getVisibleAnchorRect(element) {
    const raw = element.getBoundingClientRect();
    const clip = getClipBounds(element);
    let left = Math.max(raw.left, clip.left);
    const top = Math.max(raw.top, clip.top);
    const right = Math.min(raw.right, clip.right);
    const bottom = Math.min(raw.bottom, clip.bottom);

    if (clip.scrollParent && raw.left < clip.left - 1) {
        const stickies = clip.scrollParent.querySelectorAll(".sticky");
        for (const node of stickies) {
            if (node === element || element.contains(node) || node.contains(element)) {
                continue;
            }

            const stickyRect = node.getBoundingClientRect();
            const overlapsVertically = stickyRect.bottom > top && stickyRect.top < bottom;
            if (overlapsVertically && stickyRect.left <= left + 1 && stickyRect.right > left) {
                left = stickyRect.right;
            }
        }
    }

    return {
        left,
        top,
        right,
        bottom,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
        leftClipped: raw.left < clip.left - 1,
    };
}

function Tooltip({
    content,
    children,
    className = "z-20",
    tooltipClassName = "",
    position = "bottom-left",
    layoutClassName = "inline-flex",
    style,
}) {
    const anchorRef = useRef(null);
    const tooltipRef = useRef(null);
    const pointerXRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [coords, setCoords] = useState(null);

    const preferredPosition = useMemo(() => position, [position]);
    const [effectivePosition, setEffectivePosition] = useState(position);

    useEffect(() => {
        setEffectivePosition(preferredPosition);
    }, [preferredPosition]);

    const computeCoords = useCallback(
        (pos) => {
            if (!anchorRef.current) {
                return null;
            }

            const rect = getVisibleAnchorRect(anchorRef.current);
            const pointerX = pointerXRef.current;
            const anchorLeft =
                rect.leftClipped &&
                pointerX != null &&
                pointerX >= rect.left &&
                pointerX <= rect.right
                    ? pointerX
                    : rect.left;

            if (pos === "right") {
                return {
                    left: rect.right + VIEWPORT_MARGIN,
                    top: rect.top + rect.height / 2,
                    transform: "translateY(-50%)",
                };
            }

            if (pos === "left") {
                return {
                    left: rect.left - VIEWPORT_MARGIN,
                    top: rect.top + rect.height / 2,
                    transform: "translate(-100%, -50%)",
                };
            }

            if (pos === "top-left") {
                return {
                    left: anchorLeft,
                    top: rect.top - VIEWPORT_MARGIN,
                    transform: "translateY(-100%)",
                };
            }

            return {
                left: anchorLeft,
                top: rect.bottom + VIEWPORT_MARGIN,
                transform: "none",
            };
        },
        []
    );

    const updatePosition = useCallback(() => {
        const next = computeCoords(effectivePosition);
        if (next) {
            setCoords(next);
        }
    }, [computeCoords, effectivePosition]);

    useEffect(() => {
        if (!isVisible) {
            return undefined;
        }

        setEffectivePosition(preferredPosition);
        const next = computeCoords(preferredPosition);
        if (next) {
            setCoords(next);
        }

        const handleUpdate = () => {
            updatePosition();
        };

        window.addEventListener("scroll", handleUpdate, true);
        window.addEventListener("resize", handleUpdate);

        return () => {
            window.removeEventListener("scroll", handleUpdate, true);
            window.removeEventListener("resize", handleUpdate);
        };
    }, [isVisible, preferredPosition, computeCoords, updatePosition]);

    useLayoutEffect(() => {
        if (!isVisible || !tooltipRef.current || !anchorRef.current) {
            return;
        }

        const anchorRect = getVisibleAnchorRect(anchorRef.current);
        let nextPosition = effectivePosition;

        if (preferredPosition === "bottom-left" || preferredPosition === "top-left") {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - anchorRect.bottom;
            const spaceAbove = anchorRect.top;
            const wouldOverflowBelow =
                tooltipRect.height + VIEWPORT_MARGIN > spaceBelow;
            const wouldOverflowAbove =
                tooltipRect.height + VIEWPORT_MARGIN > spaceAbove;

            if (preferredPosition === "bottom-left" && wouldOverflowBelow && !wouldOverflowAbove) {
                nextPosition = "top-left";
            } else if (preferredPosition === "top-left" && wouldOverflowAbove && !wouldOverflowBelow) {
                nextPosition = "bottom-left";
            }
        }

        if (preferredPosition === "right") {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const overflowsRight =
                tooltipRect.right > window.innerWidth - VIEWPORT_MARGIN;
            const spaceLeft = anchorRect.left - VIEWPORT_MARGIN;

            if (overflowsRight && spaceLeft >= tooltipRect.width) {
                nextPosition = "left";
            } else if (overflowsRight) {
                nextPosition = "bottom-left";
            }
        }

        if (nextPosition !== effectivePosition) {
            setEffectivePosition(nextPosition);
            const nextCoords = computeCoords(nextPosition);
            if (nextCoords) {
                setCoords(nextCoords);
            }
            return;
        }

        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        let deltaX = 0;
        let deltaY = 0;

        if (tooltipRect.right > window.innerWidth - VIEWPORT_MARGIN) {
            deltaX = window.innerWidth - VIEWPORT_MARGIN - tooltipRect.right;
        }
        if (tooltipRect.left < VIEWPORT_MARGIN) {
            deltaX = VIEWPORT_MARGIN - tooltipRect.left;
        }
        if (tooltipRect.bottom > window.innerHeight - VIEWPORT_MARGIN) {
            deltaY = window.innerHeight - VIEWPORT_MARGIN - tooltipRect.bottom;
        }
        if (tooltipRect.top < VIEWPORT_MARGIN) {
            deltaY = VIEWPORT_MARGIN - tooltipRect.top;
        }
        if (anchorRect.leftClipped && tooltipRect.left + deltaX < anchorRect.left) {
            deltaX = anchorRect.left - tooltipRect.left;
        }

        if (deltaX !== 0 || deltaY !== 0) {
            setCoords((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    left: prev.left + deltaX,
                    top: prev.top + deltaY,
                };
            });
        }
    }, [isVisible, preferredPosition, effectivePosition, computeCoords, content]);

    if (!content) {
        return children;
    }

    return (
        <span
            ref={anchorRef}
            className={`relative overflow-visible ${layoutClassName} ${className}`.trim()}
            style={style}
            onMouseEnter={(event) => {
                pointerXRef.current = event.clientX;
                setEffectivePosition(preferredPosition);
                const next = computeCoords(preferredPosition);
                if (next) {
                    setCoords(next);
                }
                setIsVisible(true);
            }}
            onMouseLeave={() => {
                pointerXRef.current = null;
                setIsVisible(false);
            }}
            onFocus={() => {
                pointerXRef.current = null;
                setEffectivePosition(preferredPosition);
                const next = computeCoords(preferredPosition);
                if (next) {
                    setCoords(next);
                }
                setIsVisible(true);
            }}
            onBlur={() => {
                setIsVisible(false);
            }}
        >
            {children}
            {isVisible && coords && createPortal(
                <span
                    ref={tooltipRef}
                    className={`pointer-events-none fixed z-[9999] w-max max-w-[min(32rem,calc(100vw-1rem))] whitespace-normal break-words rounded-xl border border-mute-200 bg-white px-3.5 py-2.5 text-xs font-normal text-primary-500 shadow-lg ${tooltipClassName}`.trim()}
                    role="tooltip"
                    style={{
                        left: `${coords.left}px`,
                        top: `${coords.top}px`,
                        transform: coords.transform,
                    }}
                >
                    {content}
                </span>,
                document.body
            )}
        </span>
    );
}

export default Tooltip;
